import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { Point, viewportToStage } from "frontend/utils/math-utils";
import { Group, Line } from "react-konva";
import consts from "shared/consts";
import { OrgChartElement, OrgChartOrientation, OrgChartViewType } from "shared/datamodel/schemas/org-chart";
import { enablePatches, produceWithPatches } from "immer";
import {
  boardAtom,
  isAnyKindOfExportAtom,
  orgChartNodeDataForSidePanel,
  selectedElementIdsAtom,
  SidepanelType,
  sidePanelType,
  transformerRefAtom,
  userAtom,
  utilsAtom,
} from "state-atoms";
import * as utils from "../orgchart-utils";
import * as treeUtils from "../shared/tree-layout-utils";
import { calculateFieldLayout } from "./utils";
import { Degrees } from "frontend/utils/transform";
import { MindmapNewNodeButton } from "frontend/canvas-designer-new/utility-elements/mindmap-sockets";
import { selectAtom } from "jotai/utils";
import { filter, keys, prop } from "rambda";
import { minIndexBy, noop } from "frontend/utils/fn-utils";
import { OrgChartNode, OrgChartViewOnlyNode } from "./orgchart-node";
import tracking from "frontend/tracking";
import {
  OrgchartCollapseButtonHorizontal as CollapseButtonH,
  OrgchartCollapseButtonVertical as CollapseButtonV,
} from "../shared/orgchart-collapse-subtree-button";
import type Konva from "konva";
import { useOrgchartFileUploader } from "frontend/hooks/use-orgchart-uploader";
import { UploadedUppyFile } from "@uppy/core";
import { useEventListener } from "usehooks-ts";
import { lightenColor } from "frontend/utils/color-utils";
import {
  CollapsedButtonData,
  computeTreeData,
  computeTreeLayout,
  computeDescendants,
  EdgeData,
  NodeData,
} from "./org-chart-skeleton";
import { hierarchy, tree } from "d3-hierarchy";
import SimpleConnector from "../../connector/simple-connector";
import { OrgChartTransformer } from "../shared/org-chart-trasnformer";
import BoundingBox from "frontend/geometry/bounding-box";
import { arrayEq, getNodeDrawProps, isOrgChartRootSelectedAtom } from "../shared/utils";
import { CurrentAnchorMode, DragDropThreshold, SeparationLayers, SeparationSiblings } from "../shared/consts";
import { IKonvaDragEvent, Mutator, SliceParameters } from "../shared/types";
import { Sizes } from "frontend/data/shapes/sizes";

enablePatches();

function OrgChartCanvasElement({
  uniqueId,
  elementFromDb,
  patchCanvasElement,
  editable,
  onResize,
}: {
  uniqueId: string;
  elementFromDb: OrgChartElement;
  patchCanvasElement: (args: { id: string; patch: any; inversePatch?: any; details?: any }) => void;
  editable: boolean;
  onResize: (id: string, position: Point, scaleX: number, scaleY: number, rotation: number) => void;
}) {
  const board = useAtomValue(boardAtom);
  const user = useAtomValue(userAtom);
  const isExporting = useAtomValue(isAnyKindOfExportAtom);
  const [nodeForSidePanel, setNodeForSidePanel] = useAtom(orgChartNodeDataForSidePanel);
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const rootid = uniqueId.split("-")[1];
  const [editingFieldId, setEditingFieldId] = useState<string>("");
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);

  const photoUploader = useOrgchartFileUploader();
  const canvasUtils = useAtomValue(utilsAtom(board?.documentId));
  const transformerRef = useAtomValue(transformerRefAtom);
  const dragRef = useRef<any>(null);
  const dropMarkRef = useRef<any>(null);
  const addButtonsRef = useRef<Konva.Group | null>(null);

  let element = utils.cleanupElement(elementFromDb);
  const sizes = Sizes.get().orgChart;
  const PictureRadius = sizes.thumbnailRadius;
  const NodeWidth = sizes.nodeWidth;
  const ADD_NODE_BUTTON_DISTANCE = PictureRadius + 12;

  // Create an atom that only contains our orgchart nodes from the selected-element-ids atom
  const myselectedAtom = useMemo(
    () =>
      selectAtom(
        selectedElementIdsAtom,
        filter((id: string) => id.includes(rootid)),
        arrayEq
      ),
    []
  );
  const mySelectedNodes = useAtomValue(myselectedAtom);
  // Now only when our nodes are selected, we'll re-render :-)

  // this is an atom that tells us if the root of _any_ orgchart is selected
  const isOrgChartRootSelected = useAtomValue(isOrgChartRootSelectedAtom);

  const visibleFields = element.orderOfFields.filter((id) => element.extraFields?.[id]?.hidden == false);
  const itemView = utils.getItemView(element);
  const withPicture = itemView == OrgChartViewType.Default;

  // Pre-calculate all node heights once to avoid redundant calculations
  const preComputedNodeHeights = useMemo(() => {
    const heights = new Map<string, number>();

    // Calculate root node height
    const rootNodeData = element.data.root || {};
    const rootLayout = calculateFieldLayout(rootNodeData, visibleFields, withPicture, sizes);
    heights.set("root", rootLayout.totalContentHeight);

    // Calculate heights for all other nodes
    Object.keys(element.data).forEach((nodeId) => {
      if (nodeId !== "root") {
        const nodeData = element.data[nodeId] || {};
        const layout = calculateFieldLayout(nodeData, visibleFields, withPicture, sizes);
        heights.set(nodeId, layout.totalContentHeight);
      }
    });

    return heights;
  }, [element.data, visibleFields, withPicture]);

  const height = preComputedNodeHeights.get("root") || 0;

  function track(action: string) {
    tracking.trackEvent(consts.TRACKING_CATEGORY.ORG_CHART, action, board?.documentId, board?.accountId, user?.id);
  }

  // a small auxiliary function to call a mutator and send the patch onwards
  function patch<T extends Mutator>(fn: T, ...args: SliceParameters<T>) {
    const mutation = fn(element, ...args);
    if (mutation[1].length) {
      patchCanvasElement({ id: uniqueId, patch: mutation[1], inversePatch: mutation[2] });
    }
  }

  const openUploadModal = photoUploader ? (nodeId: string) => photoUploader(uniqueId, nodeId, onUploadComplete) : null;

  function onUploadComplete(file: UploadedUppyFile<any, any>) {
    track("orgchart_picture_upload_done");
    patch(utils.updatePhotoInNode, file.meta.nodeId, file.meta.url);
  }

  const setEditFieldWithTracking = (fieldId: string) => {
    if (!editable) return;
    if (fieldId) {
      track("orgchart_edit_field");
    }
    setEditingFieldId(fieldId);
  };

  function isEditorOpenForNode(nodeId: string) {
    return (
      openedSidePanel == SidepanelType.orgChartNodeInfo &&
      Boolean(nodeForSidePanel && nodeForSidePanel.rootid == uniqueId && nodeForSidePanel.selectedNode == nodeId)
    );
  }

  function toggleEditor(nodeId: string) {
    if (isEditorOpenForNode(nodeId)) {
      setOpenedSidePanel((prev) =>
        prev == SidepanelType.orgChartNodeInfo ? SidepanelType.none : SidepanelType.orgChartNodeInfo
      );
    } else {
      setNodeForSidePanel({ rootid: uniqueId, selectedNode: nodeId, editable });
      setOpenedSidePanel(SidepanelType.orgChartNodeInfo);
    }
  }

  // Cut/Copy/Paste operations
  interface OrgChartClipboard {
    miniclipboard: null | (ReturnType<typeof utils.copyNodes> & { originalSelected: string[] });
    mySelectedNodes: string[];
    element: OrgChartElement;
  }
  const clipboardData = useRef<OrgChartClipboard>({
    miniclipboard: null,
    mySelectedNodes,
    element,
  });
  clipboardData.current.mySelectedNodes = mySelectedNodes;
  clipboardData.current.element = element;

  function copySelected(e: any) {
    // if any html element is focused, don't copy
    if (document.activeElement != document.body) return null;
    // reset the internal clipboard
    clipboardData.current.miniclipboard = null;
    const { mySelectedNodes, element } = clipboardData.current;
    // we don't copy if the root is selected - canvas-stage will copy the entire chart
    if (mySelectedNodes.some(utils.isIdOfRoot)) return null;
    // from this point on we handle the event
    e.preventDefault();
    e.stopPropagation();
    // get the short ids of the selected nodes
    const nodeIds = mySelectedNodes.map((id) => utils.parseNodeId(id)!.nodeId);
    // just one final check...
    if (nodeIds.length === 0 || nodeIds.includes("root")) {
      // I've seen this case happen, though it should not.
      // should investigate
      return null;
    }
    // copy the information we'll need, and return the ids we copied
    clipboardData.current.miniclipboard = { ...utils.copyNodes(element, nodeIds), originalSelected: mySelectedNodes };
    return nodeIds;
  }

  useEventListener("copy" as keyof WindowEventMap, editable ? copySelected : noop);
  useEventListener(
    "cut" as keyof WindowEventMap,
    editable
      ? (e) => {
          const copy = copySelected(e);
          if (copy) {
            patch(utils.deleteNodes, copy);
            canvasUtils?.clearSelectedElementIds();
          }
        }
      : noop
  );
  useEventListener(
    "paste" as keyof WindowEventMap,
    editable
      ? (e) => {
          if (document.activeElement != document.body) return;
          if (!clipboardData.current.miniclipboard) return;

          e.preventDefault();
          e.stopPropagation();
          const { data, layout, roots, originalSelected } = clipboardData.current.miniclipboard;

          if (mySelectedNodes.length > 0 && originalSelected && arrayEq(mySelectedNodes, originalSelected)) {
            // paste the copied data as siblings
            const newIds: any = {};
            const getNewId = (id: string) => (newIds[id] ??= utils.createNodeId());
            const mutation = produceWithPatches(element, (draft) => {
              for (const id in data) {
                const newid = getNewId(id);
                draft.data[newid] = { ...data[id], id: newid };
                draft.layout[newid] = {
                  id: newid,
                  collapsed: draft.layout[id].collapsed,
                  childrenIds: draft.layout[id].childrenIds.map(getNewId),
                };
              }
              roots.forEach((root: string) => {
                draft.layout[utils.findParent(draft, root)!].childrenIds.push(getNewId(root));
              });
            });
            patchCanvasElement({
              id: uniqueId,
              patch: mutation[1],
              inversePatch: mutation[2],
              details: { action: "orgchart-add-node", count: keys(newIds).length },
            });
          } else if (mySelectedNodes.length == 1) {
            // put all the copied data as child of this single node
            const newParentForPaste = utils.parseNodeId(mySelectedNodes[0])!.nodeId;
            const newIds = utils.createNewIds(data) as Record<string, string>;
            const mutation = produceWithPatches(element, (draft) => {
              for (const id in newIds) {
                draft.data[newIds[id]] = { ...data[id], id: newIds[id] };
                draft.layout[newIds[id]] = {
                  id: newIds[id],
                  collapsed: layout[id].collapsed,
                  childrenIds: layout[id].childrenIds.map((c: string) => newIds[c]),
                };
              }
              const newRoots = roots.map((id: string) => newIds[id]);
              draft.layout[newParentForPaste].childrenIds.push(...newRoots);
            });
            patchCanvasElement({
              id: uniqueId,
              patch: mutation[1],
              inversePatch: mutation[2],
              details: { action: "orgchart-add-node", count: keys(newIds).length },
            });
          }
        }
      : noop
  );

  // Custom tree computation for horizontal layout with variable node heights
  function computeTreeDataWithVariableHeights(
    layout: any,
    rootId: string,
    orientation: OrgChartOrientation,
    defaultHeight: number
  ) {
    // First, calculate individual node heights
    const nodeHeights = new Map<string, number>();

    // Calculate heights for all nodes in the layout
    const calculateNodeHeights = (nodeId: string) => {
      const node = layout[nodeId];
      if (!node) return;

      const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;
      nodeHeights.set(nodeId, nodeHeight);

      if (node.childrenIds && !node.collapsed) {
        for (const childId of node.childrenIds) {
          calculateNodeHeights(childId);
        }
      }
    };

    calculateNodeHeights("root");

    // Use the standard tree computation but with a custom separation function
    const layoutUtil = treeUtils.TreeLayoutHelper.from(orientation, NodeWidth, defaultHeight, SeparationLayers);
    const convert = treeUtils.LayoutProps[orientation].convert;

    const descendants = computeDescendants(layout, rootId);

    // Create hierarchy with custom node sizes
    const root = hierarchy(layout[rootId], (d) =>
      !d.childrenIds || d.collapsed ? null : d.childrenIds.map((id: any) => layout[id]).filter(Boolean)
    );

    // For now, use a simpler approach - use default node size but custom separation
    const treeLayoutMaker = tree<any>()
      .nodeSize(layoutUtil.getNodeSizeForD3tree())
      .separation((a: any, b: any) => {
        // Custom separation based on the heights of adjacent nodes
        const heightA = nodeHeights.get(a.data.id) || defaultHeight;
        const heightB = nodeHeights.get(b.data.id) || defaultHeight;
        const avgHeight = (heightA + heightB) / 2;
        const baseHeight = defaultHeight;

        // Adjust separation based on average height of adjacent nodes
        return SeparationSiblings * (avgHeight / baseHeight);
      });

    const treeLayout = treeLayoutMaker(root);
    const collapseButtonOffset = layoutUtil.getNodeSocket("toLeaves", 12 * sizes.widgetScale);
    const selfOfs = layoutUtil.getNodeSocket("toRoot", 1);
    const parentOfs = layoutUtil.getNodeSocket("toLeaves", 1);

    // Process the tree data similar to the original computeTreeData
    const nodesData = [] as NodeData[];
    let rootNode: NodeData | undefined;

    treeLayout.eachAfter((node) => {
      const [x, y] = convert(node.x, node.y);
      const { total, direct } = descendants[node.data.id];
      const depth = node.depth;

      if (node.data.id == rootId) {
        rootNode = {
          id: node.data.id,
          x,
          y,
          descendants: total,
          directChildren: direct,
          collapsed: !node.children,
          depth,
        };
        return;
      }

      const nodedata: NodeData = {
        id: node.data.id,
        x,
        y,
        parentId: node.parent?.data.id,
        descendants: total,
        directChildren: direct,
        collapsed: !node.children,
        depth,
      };

      if (node.parent) {
        const [parentX, parentY] = convert(node.parent.x, node.parent.y);
        nodedata.parentId = node.parent.data.id;
        nodedata.edgeToParent = {
          parentAnchor: {
            x: parentX + parentOfs.x,
            y: parentY + parentOfs.y,
            rotation: parentOfs.rotation as Degrees,
          },
          childAnchor: {
            x: x + selfOfs.x,
            y: y + selfOfs.y,
            rotation: selfOfs.rotation as Degrees,
          },
        };
      }
      nodesData.push(nodedata);
    });

    return {
      nodesData,
      rootNode: rootNode!,
      collapseButtonOffset,
      descendants,
    };
  }

  const { nodesData, rootNode } = useMemo(() => {
    // For horizontal layout, we need custom tree computation that accounts for individual node heights
    if (element.orientation === OrgChartOrientation.LeftToRight) {
      return computeTreeDataWithVariableHeights(element.layout, "root", element.orientation, height);
    } else {
      // For vertical layout, use the standard computation
      return computeTreeData(element.layout, "root", element.orientation, height, NodeWidth, SeparationLayers);
    }
  }, [element.layout, element.orientation, element.data, height, visibleFields, withPicture]);

  function computeOffsetToTopLeftAnchor() {
    let offsetToLeft = 0,
      offsetToBottom = 0;
    if (nodesData.length > 0) {
      if (element.orientation == OrgChartOrientation.TopToBottom) {
        const leftmost = minIndexBy(prop("x"), nodesData);
        offsetToLeft = -leftmost[0].x;
      } else {
        const topmost = minIndexBy(prop("y"), nodesData);
        offsetToBottom = -topmost[0].y;
      }
    }
    return { x: offsetToLeft, y: offsetToBottom };
  }

  const anchor = computeOffsetToTopLeftAnchor();

  // this component renders the org chart in 'topleft' mode, where the element's (x,y) is the top left
  // of the bounding box of the org-chart tree. If we've just read a 'root' mode element, we need to
  // adjust the x,y so it doesn't move on the canvas
  useEffect(() => {
    if (element.anchor != "topleft" && CurrentAnchorMode == "topleft") {
      const [, patch, inversePatch] = produceWithPatches(element, (draft) => {
        draft.x -= anchor.x;
        draft.y -= anchor.y;
        draft.anchor = "topleft";
      });
      patchCanvasElement({ id: uniqueId, patch, inversePatch });
    }
  }, []);

  function createMutationForAddingNode(baseNodeId: { isRoot: boolean; nodeId: string }, relation: utils.Relationship) {
    if (baseNodeId.isRoot && relation != "child") {
      throw new Error("root node can only have children");
    }
    const [, patch, inversePatch] = produceWithPatches(element, (draft) => {
      utils.createAndInsertNode(draft, baseNodeId.nodeId, relation);
      if (relation == "child") {
        draft.layout[baseNodeId.nodeId].collapsed = false;
      }
    });
    return [patch, inversePatch];
  }

  function createNewNode(baseNodeId: { isRoot: boolean; nodeId: string }, relation: utils.Relationship) {
    try {
      const [patch, inversePatch] = createMutationForAddingNode(baseNodeId, relation);
      // execute the mutation
      patchCanvasElement({
        id: uniqueId,
        patch,
        inversePatch,
        details: { action: "orgchart-add-node" },
      });
    } catch (error) {
      console.error(error);
    }
  }

  function createNewNodeFromButton(nodeId: string, relation: utils.Relationship) {
    track("orgchart_add_element_clicked");
    createNewNode({ isRoot: nodeId == "root", nodeId }, relation);
  }

  useEventListener(
    "keydown",
    editable
      ? (e: KeyboardEvent) => {
          if (e.key == "Enter" && (e.target as HTMLElement).nodeName == "BODY" && mySelectedNodes.length == 1) {
            const nodeId = utils.parseNodeId(mySelectedNodes[0]);
            if (!nodeId) {
              console.error("failed to parse selected node id");
              return;
            }
            createNewNode(nodeId, "child"); // sibling-after
          }
        }
      : noop
  );

  const layoutUtil = treeUtils.TreeLayoutHelper.from(element.orientation, NodeWidth, height, SeparationLayers);

  // We want to highlight the path from selected nodes to the root
  // so we mark the selected nodes and their ancestors
  // TODO: we don't really need to save nodesToHighlight..
  // we just want it for getDrawProps
  let nodesToHighlight: Record<string, boolean> = {};
  if (mySelectedNodes.length && !isExporting) {
    nodesToHighlight = utils.getAncestors(
      nodesData,
      mySelectedNodes.map((id) => utils.parseNodeId(id)!.nodeId)
    );
  }

  // Helper to walk the tree
  function* walkTreeBfs(root: any): Generator<any> {
    const queue = [root];
    while (queue.length > 0) {
      const node = queue.shift()!;
      yield node;
      if (node.children) {
        queue.push(...node.children);
      }
    }
  }

  // Helper function to calculate drag drop targets with dynamic node heights
  function calculateDynamicDragDropTargets(
    tree: any,
    orientation: OrgChartOrientation,
    nodeWidth: number,
    nodesDataArray: NodeData[],
    maxHeights: Map<number, number>,
    spacingAdjustments: Map<number, number>
  ) {
    const connections: Array<{
      x: number;
      y: number;
      dir: utils.Relationship;
      node: any;
    }> = [];

    for (const treeNode of walkTreeBfs(tree)) {
      const nodeId = treeNode.data.id;

      // Find the corresponding node data with adjusted position
      let adjustedX = treeNode.x;
      let adjustedY = treeNode.y;

      if (nodeId === "root") {
        // Root node position stays the same
        adjustedX = 0;
        adjustedY = 0;
      } else {
        // Find the node in nodesDataArray to get its depth
        const nodeInfo = nodesDataArray.find((n) => n.id === nodeId);
        if (nodeInfo) {
          const depth = nodeInfo.depth || 0;
          const spacingAdjustment = spacingAdjustments.get(depth) || 0;

          // Get the actual node height
          const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;
          const maxHeightForLevel = maxHeights.get(depth) || nodeHeight;

          // Apply the same positioning adjustments as in renderNode
          adjustedX = nodeInfo.x;
          adjustedY = nodeInfo.y + spacingAdjustment - maxHeightForLevel / 2 + nodeHeight / 2;
        }
      }

      // Calculate drop target positions based on actual node dimensions
      const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;

      // Create layout util with actual node height for accurate positioning
      const nodeLayoutUtil = treeUtils.TreeLayoutHelper.from(orientation, nodeWidth, nodeHeight, SeparationLayers);

      // Add drop targets at proper positions relative to the node
      const toLeaves = nodeLayoutUtil.getNodeSocket("toLeaves", 12 * sizes.widgetScale);
      const siblingAfter = nodeLayoutUtil.getNodeSocket("siblingAfter", 15);
      const siblingBefore = nodeLayoutUtil.getNodeSocket("siblingBefore", 15);

      // Add child drop target
      connections.push({
        x: adjustedX + toLeaves.x,
        y: adjustedY + toLeaves.y,
        dir: "child" as utils.Relationship,
        node: treeNode,
      });

      // Add sibling drop targets (not for root)
      if (treeNode.parent) {
        connections.push(
          {
            x: adjustedX + siblingAfter.x,
            y: adjustedY + siblingAfter.y,
            dir: "sibling-after" as utils.Relationship,
            node: treeNode,
          },
          {
            x: adjustedX + siblingBefore.x,
            y: adjustedY + siblingBefore.y,
            dir: "sibling-before" as utils.Relationship,
            node: treeNode,
          }
        );
      }
    }

    return connections;
  }

  function onDragStart(ev: IKonvaDragEvent) {
    addButtonsRef.current?.visible(false);
    const node = ev.target;
    const nodeId: string = node.attrs.nodeId;
    if (!nodeId) {
      console.warn("Can't find node-id on drag target; won't drag");
      return;
    }
    const x0 = ev.evt.clientX,
      y0 = ev.evt.clientY;
    const nodesGroup = node.getParent() as Konva.Group;
    const wrapper = nodesGroup.getParent() as unknown as Konva.Group;
    const descendants = utils.collectDescendants(element.layout, nodeId, false);
    const subtree = nodesGroup.getChildren((x) => descendants.includes(x.attrs.nodeId)).toArray();
    const edgesGroup = (wrapper &&
      (wrapper.findOne as any)?.((n: any) => n.name && n.name() === "edges-group")) as Konva.Group | null;
    const edges = edgesGroup
      ? edgesGroup.getChildren((x) => x.attrs.type == "edge" && descendants.includes(x.attrs.childId)).toArray()
      : [];

    // move the dragged subtree to top, so you can see it above the others
    node.moveToTop();
    subtree.forEach((x) => x.moveToTop());

    // Get the actual height of the node being dragged
    const draggedNodeHeight = preComputedNodeHeights.get(nodeId) || 0;

    // Create layout util with the dragged node's actual height
    const draggedNodeLayoutUtil = treeUtils.TreeLayoutHelper.from(
      element.orientation,
      NodeWidth,
      draggedNodeHeight,
      SeparationLayers
    );

    // Calculate drag targets with proper positioning based on dynamic heights
    const treeLayout = computeTreeLayout(element.layout, "root", draggedNodeLayoutUtil.getNodeSizeForD3tree());
    // Create custom targets that account for dynamic node heights and spacing adjustments
    const customTargets = calculateDynamicDragDropTargets(
      treeLayout,
      element.orientation,
      NodeWidth,
      nodesData,
      maxHeightsByLevel,
      levelSpacingAdjustments
    );

    dragRef.current ||= {
      draggedByUser: new Set(),
      dragEnded: 0,
      targets: customTargets,
      invalidDrops: new Set(),
      draggedNodeHeight: draggedNodeHeight, // Store the dragged node height
    };

    // Hide the parent edge, which lives in the sibling group named "edges-group"
    let edgeToParent: Konva.Node | null = null;
    if (edgesGroup) {
      edgeToParent =
        edgesGroup.getChildren((x: any) => x.attrs.type == "edge" && x.attrs.childId == nodeId).toArray()[0] || null;
      if (edgeToParent) {
        edgeToParent.visible(false);
      }
    }

    // Use the dragged node's layout util for marker points
    const markerPointsPerpendicular = draggedNodeLayoutUtil.nodeEdgePerpMainAxis();
    const markerPointsCrossAxis = draggedNodeLayoutUtil.nodeEdgePerpCrossAxis();

    dragRef.current.draggedByUser.add(node);
    dragRef.current.invalidDrops.add(node.attrs.nodeId);
    subtree.forEach((x) => dragRef.current.invalidDrops.add(x.attrs.nodeId));
    node.on("dragmove", onDragMove);
    node.on("dragend", onDragEnd);

    function onDragMove(ev: IKonvaDragEvent) {
      let dx = ev.evt.clientX - x0,
        dy = ev.evt.clientY - y0;
      const stage = ev.target.getStage();
      if (!stage) {
        return;
      }
      dx /= stage.scale().x;
      dy /= stage.scale().x;
      dx /= element.scaleX ?? 1;
      dy /= element.scaleY ?? 1;
      const offset = { x: -dx, y: -dy };
      // TODO: when starting to drag a new node, filter out from subtree the node, that way I don't have to check here
      for (const node of subtree) {
        if (!dragRef.current.draggedByUser.has(node)) {
          node.offset(offset);
        }
      }
      for (const edge of edges) {
        edge.offset(offset);
      }

      // this part can be throttled and done async, no need to do it here
      const dropTargets = dragRef.current.targets;
      const canvasPos = viewportToStage(
        { x: stage.position().x, y: stage.position().y, scale: stage.scale().x },
        { x: ev.evt.clientX, y: ev.evt.clientY }
      );
      // make it relative to org chart element (in position)
      const { scaleX = 1 } = element;
      canvasPos.x = canvasPos.x - (element.x + anchor.x * scaleX);
      canvasPos.y = canvasPos.y - (element.y + anchor.y * scaleX);
      // and now scale it just  like the org chart
      canvasPos.x /= scaleX;
      canvasPos.y /= scaleY;
      // now canvasPos is in the local-space of the org chart.

      // find closest node that isn't part of the dragged subtree
      let minimumDistance = Number.MAX_SAFE_INTEGER,
        best: any = null;
      for (const drop of dropTargets) {
        const { x, y, node: dropNode } = drop;
        if (dragRef.current.invalidDrops.has(dropNode.data.id)) continue;
        if (descendants.includes(dropNode.data.id)) continue;
        const distance = (canvasPos.x - x) ** 2 + (canvasPos.y - y) ** 2;
        if (distance < minimumDistance) {
          minimumDistance = distance;
          best = drop;
        }
      }
      if (minimumDistance < DragDropThreshold && best != null) {
        dropMarkRef.current.visible(true);
        dropMarkRef.current.attrs.data = best;
        dropMarkRef.current.position({ x: best.x, y: best.y });
        if (best.dir == "child") {
          dropMarkRef.current.points(markerPointsPerpendicular);
        } else {
          dropMarkRef.current.points(markerPointsCrossAxis);
        }
      } else {
        dropMarkRef.current.visible(false);
        dropMarkRef.current.attrs.data = null;
      }
    }

    // Used in onDragEnd:
    // nodes should be inserted in the order they were in.
    // if A is left of B in the tree, after dragging A should still be left of B.
    // To sort the dragged-nodes in the order they were in, I traverse the tree in-order
    // and pick the dragged nodes from this ordered traversal.
    function* traverseInOrder(root = "root"): Generator<string, void, any> {
      const node = element.layout[root];
      const children = node.childrenIds?.length ?? 0;
      const mid = Math.ceil(children / 2);
      // in our trees, children are positioned on both sides of the root, symmetrically,
      // so half are left of the root, half to the right
      // yield the left half first, then the root, then the right half
      for (let i = 0; i < mid; i++) {
        const child = node.childrenIds[i];
        yield* traverseInOrder(child);
      }
      yield root;
      for (let i = mid; i < node.childrenIds.length; i++) {
        const child = node.childrenIds[i];
        yield* traverseInOrder(child);
      }
    }

    function onDragEnd(ev: IKonvaDragEvent) {
      addButtonsRef.current?.visible(true);
      // reset everything. if we've dragged to a new location in the tree, this whole component will be rendered anew.
      // and if not, we need to reset to restore the state of the tree
      ev.target.position({ x: 0, y: 0 });
      subtree.forEach((node: Konva.Node) => node.offset({ x: 0, y: 0 }));
      edges.forEach((node: Konva.Node) => node.offset({ x: 0, y: 0 }));
      ev.target.off("dragmove", onDragMove);
      ev.target.off("dragend", onDragEnd);
      if (edgeToParent) edgeToParent.visible(true);

      // count number of end-drag; when it matches number of start-drags we can create a single mutation to change the orgchart
      if (!dragRef.current) return;
      dragRef.current.dragEnded++;
      if (dragRef.current.dragEnded == dragRef.current.draggedByUser.size) {
        const dragged = dragRef.current.draggedByUser;
        const data = dropMarkRef.current.attrs.data;
        if (data?.dir) {
          let draggedIds = [...dragged].map((x) => x.attrs.nodeId);
          // The following 2 lines filter out the nodes that are descendants of other dragged nodes,
          // by moving only the root nodes of the dragged subtrees, I keep structure.
          // TODO: this calculation is (probably) made somewhen before here, so it can be cached.
          const d2 = draggedIds.flatMap((id) => utils.collectDescendants(element.layout, id, false));
          draggedIds = draggedIds.filter((id) => !d2.includes(id));

          const inorder = [];
          for (const node of traverseInOrder()) {
            if (draggedIds.includes(node)) {
              inorder.push(node);
            }
          }
          draggedIds = inorder;

          if (data.dir == "child") {
            patch(utils.moveNodesUnderAnother, draggedIds, data.node.data.id);
          } else if (data.dir == "sibling-after") {
            patch(utils.moveNodesToRightOfAnother, draggedIds, data.node.data.id, data.node.parent.data.id);
          } else if (data.dir == "sibling-before") {
            patch(utils.moveNodesToLeftOfAnother, draggedIds, data.node.data.id, data.node.parent.data.id);
          } else {
            console.warn("unknown relationship in drop-target-marker", data);
          }
        }
        dragRef.current = null;
        dropMarkRef.current.visible(false);
        //TODO: the transformer stays in the previous location of the dragged nodes.
        // updating it doesn't help so I turn it off to hide this problem
        setTimeout(() => {
          transformerRef?.current?.nodes([]);
        }, 50);
      }
    }
  }

  const Button = element.orientation == OrgChartOrientation.TopToBottom ? CollapseButtonH : CollapseButtonV;
  const Node = editable ? OrgChartNode : OrgChartViewOnlyNode;

  const dragStart = !isOrgChartRootSelected && editable ? onDragStart : undefined;

  // Calculate max height for each level to enable top-alignment
  const maxHeightsByLevel = useMemo(() => {
    const levelHeights = new Map<number, number>();

    // Calculate actual root node height
    const rootHeight = preComputedNodeHeights.get("root") || 0;
    levelHeights.set(0, rootHeight);

    // Calculate height for each child node and track max per level
    nodesData.forEach((nodeData) => {
      if (nodeData.depth !== undefined) {
        const nodeId = nodeData.id;
        const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;

        const currentMax = levelHeights.get(nodeData.depth) || 0;
        levelHeights.set(nodeData.depth, Math.max(currentMax, nodeHeight));
      }
    });

    return levelHeights;
  }, [nodesData, preComputedNodeHeights]); // Dependencies for dynamic height calculation

  // Calculate the cumulative spacing adjustments needed for proper edge spacing
  const levelSpacingAdjustments = useMemo(() => {
    const adjustments = new Map<number, number>();
    let cumulativeAdjustment = 0;

    // For each level, calculate how much extra space is needed beyond the base height
    const maxDepth = Math.max(0, ...maxHeightsByLevel.keys());
    for (let depth = 0; depth <= maxDepth; depth++) {
      adjustments.set(depth, cumulativeAdjustment);

      // For spacing between this level and the next, we need to account for
      // half of this level's extra height and half of the next level's extra height
      const currentLevelHeight = maxHeightsByLevel.get(depth) || height;
      const currentExtraHeight = Math.max(0, currentLevelHeight - height);

      // Only add spacing if there's a next level
      if (depth < maxDepth) {
        const nextLevelHeight = maxHeightsByLevel.get(depth + 1) || height;
        const nextExtraHeight = Math.max(0, nextLevelHeight - height);

        // Add half of current level's extra height and half of next level's extra height
        cumulativeAdjustment += currentExtraHeight / 2 + nextExtraHeight / 2;
      }
    }

    return adjustments;
  }, [maxHeightsByLevel, height]);

  function renderNode(nodedata: NodeData) {
    const nodeId = nodedata.id;
    const id = utils.makeNodeId(rootid, nodedata.id);

    // Calculate dynamic height for this specific node
    const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;

    // For top-alignment: adjust the y position based on the max height for this level
    const maxHeightForLevel = maxHeightsByLevel.get(nodedata.depth || 0) || nodeHeight;

    // Use the pre-calculated spacing adjustment for this level
    const spacingAdjustment = levelSpacingAdjustments.get(nodedata.depth || 0) || 0;

    // Calculate the position with proper spacing
    const topAlignedPosition = {
      x: nodedata.x,
      y: nodedata.y + spacingAdjustment - maxHeightForLevel / 2 + nodeHeight / 2, // Adjust for top alignment and proper spacing
    };

    // Calculate collapse button position based on this node's actual height
    const nodeLayoutUtil = treeUtils.TreeLayoutHelper.from(
      element.orientation,
      NodeWidth,
      nodeHeight,
      SeparationLayers
    );
    const nodeCollapseButtonOffset = nodeLayoutUtil.getNodeSocket("toLeaves", 12 * sizes.widgetScale);

    // Hide collapse button only when editing this specific node
    const isNodeSelected = mySelectedNodes.includes(id);
    const isEditingThisNode = isNodeSelected && editingFieldId && editingFieldId !== "";
    const toggleButton =
      nodedata.directChildren > 0 && !isEditingThisNode
        ? renderCollapseButton({
            nodeId,
            x: nodeCollapseButtonOffset.x,
            y: nodeCollapseButtonOffset.y,
            direct: nodedata.directChildren,
            total: nodedata.descendants,
            collapsed: nodedata.collapsed,
          })
        : null;

    return (
      <React.Fragment key={nodeId}>
        <Node
          id={id}
          type={consts.CANVAS_ELEMENTS.ORG_CHART}
          isRoot={false}
          position={topAlignedPosition}
          width={sizes.nodeWidth}
          height={nodeHeight}
          fieldsToShow={visibleFields}
          fieldsTitle={element.extraFields}
          showImage={withPicture}
          data={element.data[nodeId]}
          nodeDrawProps={getNodeDrawProps(element, nodeId)}
          // extra props
          nodeId={nodeId}
          element={element}
          isSelected={mySelectedNodes.includes(id)}
          editingFieldId={editingFieldId}
          setEditingFieldId={setEditFieldWithTracking}
          onFieldUpdate={(fieldId: string, final: string) => patch(utils.updateFieldInNode, nodeId, fieldId, final)}
          isSidePanelOpenForMe={isEditorOpenForNode(nodeId)}
          onOpenPanel={() => toggleEditor(nodeId)}
          onClick={() => setNodeForSidePanel({ rootid: uniqueId, selectedNode: nodeId, editable })}
          onDragStart={dragStart}
          onPhotoClick={openUploadModal && editable ? () => openUploadModal(nodeId) : undefined}
          isExporting={isExporting}
          onMouseEnter={() => setHoveredElementId(id)}
          onMouseLeave={() => setHoveredElementId(null)}
          isHovered={hoveredElementId == id}
        >
          {toggleButton}
        </Node>
      </React.Fragment>
    );
  }

  function renderCollapseButton({ nodeId, x, y, direct, total, collapsed }: CollapsedButtonData) {
    const { color } = getNodeDrawProps(element, nodeId);
    const btncolor = collapsed ? color : lightenColor(color);
    return (
      <Button
        key={"collapse" + nodeId}
        belongsTo={nodeId}
        x={x}
        y={y}
        color={btncolor}
        collapsed={collapsed}
        direct={direct}
        indirect={total}
        onClick={!editable ? undefined : () => patch(utils.toggleCollapseNode, nodeId)}
      />
    );
  }

  function renderEdge({ childId }: EdgeData) {
    // Recalculate anchor points using actual dynamic node heights
    const childNodeFromData = nodesData.find((node) => node.id === childId);
    const parentNodeId = childNodeFromData?.parentId || "root";

    // Get actual node heights
    const childHeight = preComputedNodeHeights.get(childId) || 0;

    let parentHeight = height; // Default to root height
    if (parentNodeId === "root") {
      // Calculate actual root node height
      parentHeight = preComputedNodeHeights.get("root") || 0;
    } else {
      parentHeight = preComputedNodeHeights.get(parentNodeId) || 0;
    }

    // Use pre-calculated spacing adjustments for proper edge positioning
    const childDepth = childNodeFromData?.depth || 0;
    const childSpacingAdjustment = levelSpacingAdjustments.get(childDepth) || 0;

    // Get actual node center positions (accounting for top-alignment and proper spacing)
    const maxHeightForChildLevel = maxHeightsByLevel.get(childDepth) || childHeight;
    const childBaseX = childNodeFromData?.x || 0;
    const childBaseY =
      (childNodeFromData?.y || 0) + childSpacingAdjustment - maxHeightForChildLevel / 2 + childHeight / 2;

    let parentBaseX = 0;
    let parentBaseY = 0;
    if (parentNodeId === "root") {
      parentBaseX = rootNode.x;
      parentBaseY = rootNode.y;
    } else {
      const parentNodeFromData = nodesData.find((node) => node.id === parentNodeId);
      const parentDepth = parentNodeFromData?.depth || 0;

      // Use pre-calculated spacing adjustment for parent
      const parentSpacingAdjustment = levelSpacingAdjustments.get(parentDepth) || 0;

      const maxHeightForParentLevel = maxHeightsByLevel.get(parentDepth) || parentHeight;
      parentBaseX = parentNodeFromData?.x || 0;
      parentBaseY =
        (parentNodeFromData?.y || 0) + parentSpacingAdjustment - maxHeightForParentLevel / 2 + parentHeight / 2;
    }

    // Calculate correct socket offsets using actual heights
    const childLayoutUtil = treeUtils.TreeLayoutHelper.from(
      element.orientation,
      NodeWidth,
      childHeight,
      SeparationLayers
    );
    const parentLayoutUtil = treeUtils.TreeLayoutHelper.from(
      element.orientation,
      NodeWidth,
      parentHeight,
      SeparationLayers
    );

    const childSocket = childLayoutUtil.getNodeSocket("toRoot", 1);
    const parentSocket = parentLayoutUtil.getNodeSocket("toLeaves", 1);

    // Create corrected anchor points
    const correctedParentAnchor = {
      x: parentBaseX + parentSocket.x,
      y: parentBaseY + parentSocket.y,
      rotation: parentSocket.rotation as Degrees,
    };

    const correctedChildAnchor = {
      x: childBaseX + childSocket.x,
      y: childBaseY + childSocket.y,
      rotation: childSocket.rotation as Degrees,
    };

    return (
      <Group key={childId} type="edge" childId={childId}>
        <SimpleConnector
          id={childId}
          p1={correctedParentAnchor}
          p2={correctedChildAnchor}
          lineType={"curve"}
          element={{
            strokeWidth: nodesToHighlight[childId] ? 2 : 1,
            stroke: getNodeDrawProps(element, childId).color,
            dash: 0,
          }}
          curveStrength={50}
        />
      </Group>
    );
  }

  function getTransformerDimensions() {
    // Compute precise bounds from node rectangles using the same top-aligned positions used for rendering
    const bounds = new BoundingBox();

    // Include root node (account for picture above the header)

    const rootTop = anchor.y - height / 2 - (withPicture ? PictureRadius : 0);
    bounds.expandRect({
      x: anchor.x - NodeWidth / 2,
      y: rootTop,
      width: NodeWidth,
      height: height + (withPicture ? PictureRadius : 0),
    });

    // Include each child node using dynamic heights and spacing adjustments
    for (const n of nodesData) {
      const nodeId = n.id;
      const nodeHeight = preComputedNodeHeights.get(nodeId) || 0;
      const maxHeightForLevel = maxHeightsByLevel.get(n.depth || 0) || nodeHeight;
      const spacingAdjustment = levelSpacingAdjustments.get(n.depth || 0) || 0;
      const topAlignedY = n.y + spacingAdjustment - maxHeightForLevel / 2 + nodeHeight / 2;
      const rectX = anchor.x + n.x - NodeWidth / 2;
      const rectY = anchor.y + topAlignedY - nodeHeight / 2;
      bounds.expandRect({ x: rectX, y: rectY, width: NodeWidth, height: nodeHeight });
    }

    // Convert absolute bounds (relative to wrapper top-left) to transformer offsets and size
    const transformerOffsetX = -bounds.x;
    const transformerOffsetY = -bounds.y;
    const transformerWidth = bounds.width;
    const transformerHeight = bounds.height;

    return {
      transformerOffsetX,
      transformerOffsetY,
      transformerWidth,
      transformerHeight,
    };
  }

  function renderAddNodeButtons(nodesData: NodeData[], includeRoot: boolean) {
    if (!editable) return null;

    const selectedNode = mySelectedNodes.length === 1 ? mySelectedNodes[0] : null;
    const elementId = selectedNode || hoveredElementId;

    if (!elementId) return null;

    const id = utils.parseNodeId(elementId);

    if (!id) return null;

    if (id.isRoot && !includeRoot) return null;
    if (!id.isRoot && includeRoot) return null;

    // Hide add buttons only for the node being edited
    const isEditingThisNode = selectedNode && editingFieldId && editingFieldId !== "";
    if (isEditingThisNode) return null;

    let nodeId = "",
      x = 0,
      y = 0,
      nodeHeight = height; // Default to root height

    if (id.isRoot) {
      x = 0;
      y = 0;
      nodeId = "root";
      // Root node uses the dynamic height
      nodeHeight = height;
    } else {
      const node = nodesData.find((node) => node.id == id!.nodeId);
      if (!node) return null;
      nodeId = node.id;

      // Calculate the actual height for this specific node
      nodeHeight = preComputedNodeHeights.get(nodeId) || 0;

      // Use the same top-aligned position calculation as in renderNode
      const maxHeightForLevel = maxHeightsByLevel.get(node.depth || 0) || nodeHeight;

      // Use the pre-calculated spacing adjustment for this level
      const spacingAdjustment = levelSpacingAdjustments.get(node.depth || 0) || 0;

      // Apply the same top-alignment adjustment as the node
      x = node.x;
      y = node.y + spacingAdjustment - maxHeightForLevel / 2 + nodeHeight / 2;
    }

    // Create a layout util with the specific node's height for accurate button positioning
    const nodeLayoutUtil = treeUtils.TreeLayoutHelper.from(
      element.orientation,
      NodeWidth,
      nodeHeight,
      SeparationLayers
    );
    const leaves = nodeLayoutUtil.getNodeSocket("toLeaves", ADD_NODE_BUTTON_DISTANCE);
    const after = nodeLayoutUtil.getNodeSocket("siblingAfter", ADD_NODE_BUTTON_DISTANCE);
    const before = nodeLayoutUtil.getNodeSocket("siblingBefore", ADD_NODE_BUTTON_DISTANCE);

    const addNodebuttons: React.ReactNode[] = [];

    const addButton = (ofs: typeof leaves, relation: utils.Relationship) => {
      addNodebuttons.push(
        <MindmapNewNodeButton
          key={"anchor add_button_" + nodeId + relation.toString()}
          position={{ x: x + ofs.x, y: y + ofs.y }}
          onClick={() => createNewNodeFromButton(id!.nodeId, relation)}
          onMouseOver={() => setHoveredElementId(elementId)}
          onMouseOut={() => setHoveredElementId(null)}
        />
      );
    };

    addButton(leaves, "child");
    nodeId != "root" && addButton(after, "sibling-after");
    nodeId != "root" && addButton(before, "sibling-before");

    return <Group ref={addButtonsRef}>{addNodebuttons}</Group>;
  }

  const { scaleX = 1, scaleY = 1 } = element;

  const { transformerOffsetX, transformerOffsetY, transformerWidth, transformerHeight } = getTransformerDimensions();

  return (
    <>
      {/* Main wrapper group for children and buttons - follows root position */}
      <Group
        id={`${uniqueId}-wrapper`}
        name={`${uniqueId}-wrapper`}
        x={element.x}
        y={element.y}
        scaleX={scaleX}
        scaleY={scaleY}
      >
        {/* Edges group - draw first so connectors are always beneath nodes */}
        <Group x={anchor.x} y={anchor.y} listening={false} name="edges-group">
          {nodesData.map((nodedata) => {
            return nodedata.parentId ? renderEdge({ childId: nodedata.id } as any) : null;
          })}
        </Group>

        {/* Child nodes group - positioned relative to anchor */}
        <Group x={anchor.x} y={anchor.y} name="nodes-group">
          {nodesData.map(renderNode)}
          {!isExporting && <Line ref={dropMarkRef} visible={false} points={[]} stroke="blue" strokeWidth={5} />}
        </Group>

        {/* Add node buttons group - positioned relative to anchor (non-root nodes only) */}
        <Group x={anchor.x} y={anchor.y}>
          {!isExporting && <>{editable && renderAddNodeButtons(nodesData, false)}</>}
        </Group>
      </Group>

      {/* Root node - the main draggable element */}
      <Node
        key={uniqueId}
        id={uniqueId}
        type={consts.CANVAS_ELEMENTS.ORG_CHART}
        isRoot={true}
        position={anchor}
        width={sizes.nodeWidth}
        height={height}
        fieldsToShow={visibleFields}
        fieldsTitle={element.extraFields}
        showImage={withPicture}
        data={element.data.root}
        nodeDrawProps={getNodeDrawProps(element, "root")}
        // extra props
        nodeId={"root"}
        element={element}
        isSelected={mySelectedNodes.includes(uniqueId)}
        editingFieldId={editingFieldId}
        setEditingFieldId={setEditFieldWithTracking}
        onFieldUpdate={(fieldId: string, final: string) => patch(utils.updateFieldInNode, "root", fieldId, final)}
        isSidePanelOpenForMe={isEditorOpenForNode("root")}
        onOpenPanel={() => toggleEditor("root")}
        onClick={() => setNodeForSidePanel({ rootid: uniqueId, selectedNode: "root", editable })}
        onPhotoClick={openUploadModal && editable ? () => openUploadModal("root") : undefined}
        isExporting={isExporting}
        scaleX={element.scaleX ?? 1}
        scaleY={element.scaleY ?? 1}
        onMouseEnter={() => setHoveredElementId(uniqueId)}
        onMouseLeave={() => setHoveredElementId(null)}
        isHovered={hoveredElementId == uniqueId}
      >
        {rootNode.directChildren > 0 &&
          !(mySelectedNodes.includes(uniqueId) && editingFieldId) &&
          (() => {
            const rootCollapseButtonOffset = layoutUtil.getNodeSocket("toLeaves", 12 * sizes.widgetScale);
            return renderCollapseButton({
              nodeId: "root",
              x: rootCollapseButtonOffset.x,
              y: rootCollapseButtonOffset.y,
              direct: rootNode.directChildren,
              total: rootNode.descendants,
              collapsed: rootNode.collapsed,
            });
          })()}
        {/* Add node buttons for root - rendered as children so they're on top */}
        {!isExporting && <>{editable && renderAddNodeButtons(nodesData, true)}</>}
      </Node>

      <OrgChartTransformer
        element={element}
        scaleX={scaleX}
        scaleY={scaleY}
        uniqueId={uniqueId}
        rootId={rootid}
        onResize={onResize}
        transformerOffsetX={transformerOffsetX}
        transformerOffsetY={transformerOffsetY}
        transformerWidth={transformerWidth}
        transformerHeight={transformerHeight}
      />
    </>
  );
}

const MemoizedOrgChartCanvasElement = React.memo(OrgChartCanvasElement);
export default MemoizedOrgChartCanvasElement;
